import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import WalletGearModal, { PageGearButton } from './WalletGearModal';
import CommissionOwedPanel from './CommissionOwedPanel';
import PaperTestBalanceSettings from './PaperTestBalanceSettings';
import { fmtUsd } from '../utils/fmtPrice';
import { FcOpenTradesTable, FcClosedTradesTable } from './FcPaperTradeTables';
import { isSessionAuthError, SESSION_AUTH_MESSAGE } from '../utils/apiAuth';
import './CommissionOwedPanel.css';
import './OBotTrading.css';
import './WalletGearModal.css';
import './FundingCarryTrading.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

export default function FundingCarryTrading() {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [adminDetail, setAdminDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [leveragePref, setLeveragePref] = useState(2);
  const [savingLeverage, setSavingLeverage] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);

  const load = useCallback(async () => {
    if (sessionExpired) return;
    setError(null);
    try {
      const [cfg, st] = await Promise.all([
        axios.get(`${API_BASE}/funding-carry/config`),
        axios.get(`${API_BASE}/funding-carry/status`, { headers: authHeaders() }),
      ]);
      setConfig(cfg.data);
      setStatus(st.data);
      setLeveragePref(st.data.user?.leverage ?? 2);

      if (st.data.isAdmin) {
        const { data: detail } = await axios.get(`${API_BASE}/funding-carry/admin/detail`, {
          headers: authHeaders(),
        });
        setAdminDetail(detail);
      } else {
        setAdminDetail(null);
      }
    } catch (e) {
      if (isSessionAuthError(e)) {
        setSessionExpired(true);
        setError(SESSION_AUTH_MESSAGE);
      } else {
        setError(e.response?.data?.error || e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionExpired]);

  useEffect(() => {
    load();
    if (sessionExpired) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, sessionExpired]);

  const saveLeverage = async () => {
    setSavingLeverage(true);
    setError(null);
    try {
      const { data } = await axios.patch(
        `${API_BASE}/funding-carry/user/settings`,
        { leverage: Number(leveragePref) },
        { headers: authHeaders() }
      );
      setMessage(`Default oveBot leverage set to ${data.user.leverage}x.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSavingLeverage(false);
    }
  };

  const toggleBot = async (enable) => {
    setBusy(true);
    try {
      await axios.post(
        `${API_BASE}/funding-carry/bot/${enable ? 'enable' : 'disable'}`,
        { paperMode: config?.paperMode !== false },
        { headers: authHeaders() }
      );
      setMessage(enable ? 'oveBot enabled for your account.' : 'oveBot disabled.');
      setRefreshKey((k) => k + 1);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="obot-page loading">Loading oveBot…</div>;

  const userOn = !!status?.user?.botEnabled;
  const globalOn = !!status?.global?.enabled;
  const paper = status?.global?.paperMode !== false;
  const maxLev = status?.user?.maxLeverage ?? config?.maxLeverage ?? 3;
  const book = status?.book || {};
  const lots = book.positionLots || [];
  const recentClosed = status?.recentClosedTrades || [];
  const pb = status?.paperBook || {};
  const est = pb.estimatedRoundTrip || {};
  const ytd = status?.performance?.ytd;
  const showYtd = !!status?.performance?.visible;
  const isAdmin = !!status?.isAdmin;
  const fmtSigned = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    const s = fmtUsd(Math.abs(v));
    if (v > 0) return `+${s}`;
    if (v < 0) return `-${s}`;
    return s;
  };

  const PeriodRow = ({ label, row }) => (
    <tr>
      <td>{label}</td>
      <td>{fmtSigned(row?.fundingUsd)}</td>
      <td>{fmtSigned(row?.pricePnlUsd)}</td>
      <td>{fmtSigned(row?.grossPnlUsd)}</td>
      <td className="fc-fee">-{fmtUsd(row?.hlFeesUsd)}</td>
      <td className="fc-fee">-{fmtUsd(row?.commissionUsd)}</td>
      <td className={(row?.netPnlUsd || 0) >= 0 ? 'fc-pos' : 'fc-neg'}>
        <strong>{fmtSigned(row?.netPnlUsd)}</strong>
      </td>
    </tr>
  );

  return (
    <div className="obot-page">
      <header className="obot-header page-header-with-gear">
        <div className="page-header-main">
          <p>
            <Link to="/dashboard">← Trading Dashboard</Link>
          </p>
          <h1>oveBot</h1>
          <p className="subtitle">
            {config?.description ||
              'Omni Volatility Expansion — automated HL perp breakouts. You control subscription, wallet, and leverage.'}
            {config?.riskTier?.label && (
              <>
                {' '}
                Platform tier: <strong>{config.riskTier.label}</strong> (max {config.maxLeverage}x).
              </>
            )}
          </p>
        </div>
        <PageGearButton onClick={() => setWalletModalOpen(true)} label="oBot authorization" icon="wallet" />
      </header>

      <WalletGearModal open={walletModalOpen} onClose={() => setWalletModalOpen(false)} title="oBot authorization" view="obot" />

      {error && (
        <div className="obot-error">
          {error}
          <button type="button" className="obot-dismiss-btn" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}
      {message && (
        <div className="obot-success">
          {message}
          <button type="button" className="obot-dismiss-btn" onClick={() => setMessage(null)}>
            Dismiss
          </button>
        </div>
      )}

      <CommissionOwedPanel refreshKey={refreshKey} onPaid={load} />

      {paper && (
        <section className="obot-card">
          <PaperTestBalanceSettings compact />
        </section>
      )}

      <section className="obot-card obot-status-grid">
        <div>
          <span className="label">Global bot</span>
          <span className={`pill ${globalOn ? 'on' : 'off'}`}>{globalOn ? 'ON' : 'OFF'}</span>
          <p className="hint">Admin tab → oveBot.</p>
        </div>
        <div>
          <span className="label">Your subscription</span>
          <span className={`pill ${userOn ? 'on' : 'off'}`}>{userOn ? 'ENABLED' : 'DISABLED'}</span>
        </div>
        <div>
          <span className="label">Mode</span>
          <span className={`pill ${paper ? 'mode-paper' : 'mode-live'}`}>{paper ? 'PAPER' : 'LIVE'}</span>
        </div>
        <div>
          <span className="label">Your HL wallet</span>
          <span className="pill">{status?.wallet?.walletMasked || 'NOT LINKED'}</span>
        </div>
      </section>

      {showYtd && ytd && (
        <section className="obot-card fc-ytd-hero">
          <h2>
            oveBot YTD {isAdmin && paper ? '(admin · paper)' : ''}
          </h2>
          <p className="hint">Calendar year net after Hyperliquid + Omni fees. Updates as positions move.</p>
          <div className="fc-ytd-main">
            <strong className={(ytd.netPnlUsd || 0) >= 0 ? 'fc-pos' : 'fc-neg'}>{fmtSigned(ytd.netPnlUsd)}</strong>
            <span className={(ytd.netPnlPct || 0) >= 0 ? 'fc-pos' : 'fc-neg'}>
              ({ytd.netPnlPct > 0 ? '+' : ''}
              {Number(ytd.netPnlPct || 0).toFixed(2)}% vs {fmtUsd(ytd.referenceEquityUsd)} ref)
            </span>
          </div>
          <p className="hint fc-net-foot">
            {ytd.year} · {ytd.closedRoundTrips ?? 0} closed · {ytd.openPositions ?? 0} open · fees{' '}
            {fmtUsd((ytd.hlFeesUsd || 0) + (ytd.commissionUsd || 0))}
          </p>
        </section>
      )}

      {paper && (
        <section className="obot-card fc-paper-banner">
          <h2>Paper trading active</h2>
          <p className="hint">
            Simulated fills on live Hyperliquid prices — no real orders. Enable your subscription below; the engine
            opens and closes positions automatically when OVE setups trigger.
          </p>
        </section>
      )}

      {paper && !showYtd && !isAdmin && (
        <section className="obot-card">
          <p className="hint">
            <strong>YTD performance</strong> is shown when oveBot is in live mode. Paper validation runs in admin only.
          </p>
        </section>
      )}

      {paper && (isAdmin || showYtd) && pb.allTime && (
        <section className="obot-card fc-net-paper">
          <h2>Net performance (after fees){isAdmin && paper ? ' — admin paper' : ''}</h2>
          <p className="hint">
            <strong>Net</strong> = price PnL + funding − HL fees ({pb.feeMeta?.hlTakerBps ?? 2.5} bps/side) − Omni
            commission ({pb.feeMeta?.commissionBpsBuy ?? '—'}/{pb.feeMeta?.commissionBpsSell ?? '—'} bps).
          </p>
          {est.totalUsd > 0 && (
            <p className="hint fc-fee-estimate">
              Typical round trip ({fmtUsd(est.marginUsd)} × {est.leverage}x ≈ {fmtUsd(est.notionalUsd)} notional):{' '}
              <strong>~{fmtUsd(est.totalUsd)}</strong> (HL {fmtUsd(est.hlFeesUsd)} + Omni {fmtUsd(est.commissionUsd)}).
            </p>
          )}
          <table className="fc-table fc-net-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Funding</th>
                <th>Price PnL</th>
                <th>Gross</th>
                <th>HL fees</th>
                <th>Omni</th>
                <th>Net</th>
              </tr>
            </thead>
            <tbody>
              <PeriodRow label="Last 7 days" row={pb.days7} />
              <PeriodRow label="Last 30 days" row={pb.days30} />
              <PeriodRow label="All time" row={pb.allTime} />
            </tbody>
          </table>
          <p className="hint fc-net-foot">
            Round trips (all time): {pb.allTime?.roundTrips ?? 0} · Fills counted: {pb.allTime?.fillCount ?? 0} (open +
            close legs)
          </p>
        </section>
      )}

      <section className="obot-card">
        <h2>Your book (summary)</h2>
        <div className="fc-summary-grid">
          <div>
            <span className="label">Open positions</span>
            <strong>{book.openPositions ?? 0}</strong>
          </div>
          <div>
            <span className="label">Exposure (margin)</span>
            <strong>{fmtUsd(book.totalExposureUsd)}</strong>
          </div>
          <div>
            <span className="label">Price PnL (gross)</span>
            <strong className={(pb.allTime?.pricePnlUsd || 0) >= 0 ? 'fc-pos' : 'fc-neg'}>
              {fmtSigned(pb.allTime?.pricePnlUsd)}
            </strong>
          </div>
          <div>
            <span className="label">Net (all time)</span>
            <strong className={(pb.allTime?.netPnlUsd || 0) >= 0 ? 'fc-pos' : 'fc-neg'}>
              {fmtSigned(pb.allTime?.netPnlUsd)}
            </strong>
          </div>
        </div>
        {status?.global?.lastScanAt && (
          <p className="hint">Engine last active: {new Date(status.global.lastScanAt).toLocaleString()}</p>
        )}
      </section>

      {lots.length > 0 && (
        <section className="obot-card">
          <h2>Open positions</h2>
          <p className="hint">
            Each row is an <strong>OPEN</strong> leg. The trade ID matches the <strong>CLOSE</strong> row when the
            position exits.
          </p>
          <FcOpenTradesTable trades={lots} />
        </section>
      )}

      {recentClosed.length > 0 && (
        <section className="obot-card">
          <h2>Closed paper trades</h2>
          <p className="hint">Completed round trips — same trade ID as the original OPEN.</p>
          <FcClosedTradesTable trades={recentClosed} />
        </section>
      )}

      <section className="obot-card obot-leverage-settings">
        <h2>Your oBot trade leverage</h2>
        <p className="hint">
          Default leverage for oveBot trades. Platform tier:{' '}
          <strong>{status?.user?.riskTier?.label || status?.global?.riskTier?.label || 'Standard'}</strong> — admin
          cap {maxLev}x. Entries and exits are managed by the OVE engine.
        </p>
        <div className="obot-leverage-row">
          <label>
            Default leverage
            <select value={String(leveragePref)} onChange={(e) => setLeveragePref(Number(e.target.value))}>
              {Array.from({ length: maxLev }, (_, i) => i + 1).map((x) => (
                <option key={x} value={x}>
                  {x}x
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="secondary-btn" disabled={savingLeverage} onClick={saveLeverage}>
            {savingLeverage ? 'Saving…' : 'Save leverage'}
          </button>
        </div>
      </section>

      <section className="obot-card obot-actions">
        <p className="hint">
          Link your HL wallet and approve the oBot agent via the gear icon → <strong>Start oBot</strong>. Cash reserve
          and paper balance are in Settings.
        </p>
        {!userOn && (
          <button type="button" className="secondary-btn" disabled={busy || !globalOn} onClick={() => toggleBot(true)}>
            {paper ? 'Enable paper oveBot for my account' : 'Enable live oveBot'}
          </button>
        )}
        {userOn && (
          <button type="button" className="secondary-btn" disabled={busy} onClick={() => toggleBot(false)}>
            Disable my oveBot
          </button>
        )}
        <button type="button" className="secondary-btn" onClick={load}>
          Refresh
        </button>
      </section>

      {status?.isAdmin && adminDetail && (
        <section className="obot-card fc-admin-only">
          <h2>Admin — engine signals &amp; fills</h2>
          <p className="hint">Not visible to traders. Tune in Admin → oveBot.</p>
          <div className="fc-admin-tables">
            <div>
              <h3>Recent signals</h3>
              <table className="fc-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Action</th>
                    <th>Score</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(adminDetail.signals || []).slice(0, 15).map((s, i) => (
                    <tr key={`${s.ts}-${i}`}>
                      <td>{s.symbol}</td>
                      <td>
                        {s.action}
                        {s.side ? ` ${s.side}` : ''}
                      </td>
                      <td>{s.funding_apy != null ? Number(s.funding_apy).toFixed(0) : '—'}</td>
                      <td>{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
